/**
 * Role + permission matrix — the single source of truth for the access-control map
 * from PRD §2 (Auth) and the QBO design's read-only `bookkeeper` role.
 *
 * This module is PLAIN DATA on purpose: the API wires it into Better Auth's
 * `createAccessControl` / `newRole` (server-side enforcement, the real gate), and
 * the web app imports the same matrix to gate UI affordances. Both read one map.
 *
 * NOTE: role *permissions* answer "may this role touch this resource at all".
 * ROW scoping (e.g. a tech may only see their OWN visits, a tech's customer_360
 * drops money fields) is a separate concern enforced in the service layer + RLS —
 * never expressed here. See PRD §4.3 memo §2.2.
 */

export const ROLES = ['owner', 'office', 'tech', 'bookkeeper'] as const;
export type Role = (typeof ROLES)[number];

/**
 * The permission surface: resource -> the actions definable on it.
 * Mirrors the domain; the API merges this with Better Auth org-plugin default
 * statements (organization/member/invitation) at wiring time.
 */
export const STATEMENT = {
  commercialJob: ['read', 'create', 'update'],
  jobPipeline: ['read', 'manage'],
  /**
   * 🔴 **`browse` is listing or searching the WHOLE SHOP, and it is apart from `read` on
   * purpose** (CRM-14 requirement 2). A tech holds `read` to open the customer and the Work
   * Order on a stop (scoped to their own stops by the read window, CRM 008 and 009), and must
   * never hold `browse`: a tech never browses or searches the shop. It gates `customer.list`
   * and the AI tools' `customer_360` query mode on `customer`, and `job.list` and
   * `request.list` on `job` (requests ride `job`).
   *
   * A verb, not a `requireRole` list, for the reason `asset:read-fleet` and `crew:read-day`
   * give: a role list decides access somewhere this matrix can't see, and CRM-14 requirement 5
   * moves those lists into it. Unlike `job:read-board` (see `job.statusCounts`), it is a real
   * split: the bookkeeper holds `customer:browse` without `job:browse`.
   */
  customer: ['create', 'read', 'update', 'delete', 'browse'],
  property: ['create', 'read', 'update', 'delete'],
  contact: ['create', 'read', 'update', 'delete'],
  job: ['create', 'read', 'update', 'assign', 'execute', 'browse'],
  /**
   * 🔴 **`update` IS THE DISPATCH VERB, AND IT IS DELIBERATELY NOT THE WHOLE OF "may change
   * a visit".** Two authorities were split out of it on 2026-08-26. Read why before folding
   * either back in — both splits exist because something already went wrong.
   *
   * **`reschedule`** — moving ONE visit to a new instant, and nothing else. Techs hold it so
   * the next-stop card's "Running behind" control works (117). They deliberately do NOT hold
   * `update`, and the reason is not tidiness:
   *
   *   `setAssignees` is gated on `update`, and it does not check whether the CALLER may
   *   touch the visit — only that the assignee is a dispatchable member. So a tech holding
   *   `update` could put themselves on ANY visit in the shop and then read it back through
   *   `getVisit`, whose `assignedOnly` row scoping was written on the assumption that
   *   assignment is controlled by dispatch. That projection carries `property.gate_code` and
   *   `property.access_notes` — the two REDLINE fields the MCP surface refuses to select at
   *   all. Two calls, any address in the shop. It also silently removes whoever was really
   *   sent, because `setAssignees` takes the whole set.
   *
   * **`curate-files`** — removing a file somebody ELSE attached. `attachment-access.ts`
   * derives file authority from this matrix (`uploadedByMe || allows(role, gate, 'write')`),
   * and the visit gate's write half used to be `update`. While techs briefly held `update`,
   * that conferred the power to delete any other tech's site photos — the evidence a shop
   * has when a customer disputes what was found.
   *
   * ⚠️ **`curate-files` is kept even though its holders now coincide with `update`'s again.**
   * That coincidence is exactly what made the hole: the two were indistinguishable right up
   * until one of them moved. Re-merging them would re-arm the same trap for the next role
   * that is granted `update` for some unrelated reason. This is not the `job:assign`
   * anti-pattern — that verb has zero call sites; this one is read by `ATTACHMENT_SUBJECT`.
   *
   * ⚠️ A tech still removes their OWN uploads. That half is `uploadedByMe`, never a
   * permission.
   */
  visit: ['create', 'read', 'update', 'reschedule', 'execute', 'curate-files'],
  quote: ['create', 'read', 'update', 'send'],
  invoice: ['create', 'read', 'update', 'issue', 'void'],
  /**
   * `read-own` is the tech's read-back of a payment THEY recorded (ticket 038). It is a
   * separate verb from `read` on purpose: `read` is the ledger, and a role holding one must
   * not be assumed to hold the other. `owner`/`office`/`bookkeeper` hold `read`, which
   * subsumes it — see `paymentVisibility()` below, the one place that decision is made.
   */
  payment: ['create', 'read', 'read-own', 'refund'],
  agreement: ['create', 'read', 'update'],
  // V3 §6: `move` writes the stock ledger (counts, adjustments, transfers); `manage` owns
  // the location tree and which catalog items get counted at all.
  inventory: ['read', 'move', 'manage'],
  // V3 §6.7: `write` owns the asset register (add/edit/retire a unit); `tag` is the field
  // half — saying which unit a job touched. They split because a tech must be able to
  // record what they worked on without being able to rewrite the register.
  equipment: ['read', 'write', 'tag'],
  // V3 §6.8: projects are an aggregation view. No tech ever opens one.
  project: ['read', 'manage'],
  /**
   * Sonar-experience 106: COMPANY assets (vans, tools) — a different noun from the
   * customer-owned `equipment` register above. `manage` owns the register, maintenance
   * and tech↔vehicle assignments; `read` is the register. A tech's `read` is ROW scoped
   * in the service to assets assigned to them (the 032 doctrine: row scoping is never
   * expressed in this matrix).
   *
   * ⭐ `read-fleet` (112) is the whole shop's DAY: every van, its driver, and the
   * customer names, addresses and coordinates it is driving to. A separate verb on the
   * `payment:read-own` / `report:read-board` precedent, and for the same reason — a role
   * holding one must not be assumed to hold the other. A tech holds `read` and must NOT
   * get this: `map.day` was written as `requirePermission('visit','read')` for exactly
   * this payload, `visit:read` is a verb the tech holds, and the result was every visit
   * in the shop — customer, address, lat/lng — to anyone with a phone and a session.
   *
   * The alternative was a `requireRole('owner','office')` list on the procedure, which
   * decides access somewhere this matrix cannot see. 106 M3 banned exactly that from the
   * asset read path; this keeps the ban satisfiable by making the matrix able to say it.
   */
  asset: ['read', 'read-fleet', 'manage'],
  /**
   * Ops insights Phase 4: `read-day` is the crew map's day (`crew.day`): every visit on
   * today's board, its customer, city and coordinates, and where each tech is (at a job, or
   * between two). Owner and office hold it. A verb of its own on the `asset:read-fleet`
   * precedent, and for the same reason: `visit:read` is held by the tech, and a
   * `requireRole('owner','office')` list would decide access where this matrix cannot see.
   */
  crew: ['read-day'],
  // V4 §3: one-shot broadcasts to the shop's own clients. `send` is separated from `read`
  // because a broadcast is irreversible and legally the shop's — drafting it and pulling
  // the trigger on it are not the same act.
  campaign: ['read', 'send'],
  /**
   * `read` is the report catalogue — every margin, every cost rate, every customer's
   * balance (Profit by person also needs `read-people`, below). `read-board` is the shop
   * LEADERBOARD and nothing else (Sonar-experience D6): four operational numbers per person
   * over a week or a month, no per-customer detail and no money the shop made, only money
   * each person took in.
   *
   * A separate verb on the `payment:read-own` precedent, and for the same reason: a role
   * holding one must not be assumed to hold the other. The tech is the point — D6 rules
   * that techs see the board, and the alternative (a `requireRole` list on the procedure)
   * would decide access somewhere this matrix cannot see.
   *
   * `read-people` is REVENUE BY PERSON (ops insights Phase 2, Q4): how much of the shop's
   * revenue each person brought in. Profit by person sits behind it, and the Revenue page's
   * by-person section is built on it (Phase 2 ticket 03). Owner and office hold it; the
   * bookkeeper does not, although they hold `read`. That is the owner's ruling (ops insights
   * G-R4): the bookkeeper sees the shop's revenue and its service types, not what each person
   * brought in.
   *
   * ⚠️ It is NOT implied by `read`, and must not become so: the bookkeeper holds `read`, and
   * folding the two together reopens Profit by person to them. A separate verb on the
   * `payment:read-own` / `asset:read-fleet` precedent, not a `requireRole('owner','office')`
   * list: a role list decides access somewhere this matrix cannot see.
   *
   * ⭐ Who decides: `ServiceCtx` carries no role, so the report router asks
   * `can(role, 'report', 'read-people')` and passes the answer to the service, which refuses
   * or withholds with `REVENUE_BY_PERSON_REASON` (below the matrix).
   */
  report: ['read', 'read-board', 'read-people'], // catalogue, leaderboard, by person
  /**
   * Ops insights Phase 1: the shop's list of service types (CONTEXT.md "Service type").
   * `manage` adds, renames, reorders, turns off and deletes them. There is no `read`:
   * every member reads the list (pickers, the Jobs filter, the revenue reports), so
   * `serviceType.list` is a plain `orgProcedure`.
   *
   * A verb and not a `requireRole('owner','office')` list on the procedures, for the reason
   * `asset:read-fleet` gives: a role list decides access somewhere this matrix cannot see,
   * and the web card has to ask the same question the server does.
   */
  serviceType: ['manage'],
  /**
   * Ops insights Phase 5a: a problem after a visit (CONTEXT.md "Ticket"). `read` is the ticket
   * and its conversation; `create` raises one; `update` ADDS to it (a message, a photo);
   * `triage` works it (its facts: title, category, priority, reply-due; and its status: the
   * waits and reopening); `assign` names who owns it; `resolve` closes it with what fixed it
   * and a root cause (Q4: the office resolves, because the root cause matters). Giuliano,
   * 2026-10-06: a tech writes messages and photos and follows, and the office triages.
   *
   * ⚠️ A tech's `read`, `create` and `update` are ROW scoped in the service, never here: a tech
   * sees a ticket only while they hold a live assignment on a visit of its job
   * (`techOnTicketJob` in `services/ticket.ts`), so a jobless ticket is never theirs. Their
   * `create` is "Flag a problem" from their own visit; the office's "New ticket", which can
   * name any customer in the shop, refuses a row-scoped caller.
   */
  ticket: ['read', 'create', 'update', 'triage', 'resolve', 'assign'],
  /**
   * What the monitors found (CONTEXT.md "Alert", "Monitor"; ops insights Phase 7 spec §9):
   *  - `read`: the alerts board, one alert's panel, the sidebar count (Live alerts only);
   *  - `work`: take, assign, snooze, resolve, mute, unmute one's own mute, open a ticket;
   *  - `review`: the testing queue (each monitor in Testing, its alerts, Useful / Not useful),
   *    and so 6b's "Monitors — testing" reads, which show Testing alerts;
   *  - `configure`: a monitor's settings, the Live switch, unmuting anyone's mute.
   * The owner holds all four (`ALL`); the office reads and works the board; techs and the
   * bookkeeper see nothing (Giuliano, 2026-10-07).
   */
  alert: ['read', 'work', 'review', 'configure'],
  billing: ['read', 'manage'], // subscription / rate card page
  dataExport: ['run'], // honest-terms one-click full export (owner-only, F1)
  org: ['read', 'manage', 'cancel'], // settings, self-serve cancel (F2)
  member: ['read', 'invite', 'update', 'remove'],
  aiConnection: ['read', 'manage'], // Connected-AI cards / MCP client toggles (§4.3)
} as const;

export type Resource = keyof typeof STATEMENT;
export type Action<R extends Resource> = (typeof STATEMENT)[R][number];

type PermissionMap = { [R in Resource]?: readonly (typeof STATEMENT)[R][number][] };

const ALL: PermissionMap = Object.fromEntries(
  (Object.keys(STATEMENT) as Resource[]).map((r) => [r, STATEMENT[r]]),
) as PermissionMap;

/**
 * The access-control map (PRD §2):
 *  - owner     = everything, incl. billing + export + cancel
 *  - office    = dispatch / quotes / invoices / customers (no billing-manage,
 *                no data export, no org-cancel)
 *  - tech      = own schedule + job execution + photos/notes (read customer/property,
 *                execute jobs/visits); money + dispatch authoring withheld
 *  - bookkeeper= read-only accounting (invoices, payments, reports, customers) —
 *                no dispatch, no job execution, no seat charge (QBO design §4)
 */
export const ROLE_PERMISSIONS: Record<Role, PermissionMap> = {
  owner: ALL,
  office: {
    commercialJob: ['read', 'create', 'update'],
    jobPipeline: ['read', 'manage'],
    customer: ['create', 'read', 'update', 'delete', 'browse'],
    property: ['create', 'read', 'update', 'delete'],
    contact: ['create', 'read', 'update', 'delete'],
    job: ['create', 'read', 'update', 'assign', 'execute', 'browse'],
    // `reschedule` and `curate-files` listed explicitly beside `update` even though `update`
    // is the dispatch verb — the invite drift detector compares raw action SETS, and office
    // is who a tech asks for both the things they cannot do themselves.
    visit: ['create', 'read', 'update', 'reschedule', 'execute', 'curate-files'],
    quote: ['create', 'read', 'update', 'send'],
    invoice: ['create', 'read', 'update', 'issue', 'void'],
    // `read-own` is listed even though `read` subsumes it: the invite drift detector
    // compares raw action SETS, and a role that grants `tech` a verb it does not itself
    // list would be "granting what it does not hold". Explicit beats implied here.
    payment: ['create', 'read', 'read-own', 'refund'],
    agreement: ['create', 'read', 'update'],
    inventory: ['read', 'move', 'manage'],
    equipment: ['read', 'write', 'tag'],
    project: ['read', 'manage'],
    // `read-fleet` listed explicitly beside `read` — the drift detector compares raw
    // action SETS, and office is the role that hires techs.
    asset: ['read', 'read-fleet', 'manage'],
    // The crew map: the office is who answers "which job is Maya on?".
    crew: ['read-day'],
    // The office manager is the one who writes "we're closed the week of the 4th".
    campaign: ['read', 'send'],
    // `read-board` is listed even though `read` subsumes it — same rule as `payment:read-own`
    // above: the invite drift detector compares raw action SETS, and office hires techs.
    // `read-people` is its own grant; `read` does not imply it (see STATEMENT.report).
    report: ['read', 'read-board', 'read-people'],
    // Story 9: the office keeps the list so the owner does not have to.
    serviceType: ['manage'],
    // The office works every ticket: claims it, answers it, triages it, resolves it.
    ticket: ['read', 'create', 'update', 'triage', 'resolve', 'assign'],
    // The board, worked top-down every morning (Phase 7 story 1). Not `review` or `configure`:
    // judging a monitor and switching it Live are the owner's.
    alert: ['read', 'work'],
    billing: ['read'],
    org: ['read'],
    member: ['read', 'invite'],
    aiConnection: ['read'],
  },
  tech: {
    // The service limits the commercial projection to assigned execution work and omits money.
    commercialJob: ['read'],
    customer: ['read'],
    property: ['read'],
    contact: ['read'],
    job: ['read', 'execute'],
    /**
     * 🔴 **`reschedule`, DELIBERATELY NOT `update`. Do not "simplify" this to `update`.**
     *
     * The ask was one control: the next-stop card's "Running behind — push this stop" (117).
     * `visit:update` was granted for it on 2026-08-26 and **narrowed back the same day**,
     * because holding it let a tech read any address in the shop:
     *
     *   1. `visit.setAssignees` is gated `update` and checks only that the ASSIGNEE is a
     *      dispatchable member — never that the CALLER may touch this visit. A tech could
     *      put themselves on any visit in the org.
     *   2. `getVisit`'s `assignedOnly` row scoping then let them read it, because that
     *      scoping was written assuming assignment is controlled by dispatch.
     *   3. That projection carries `property.gate_code` and `property.access_notes` — the
     *      two REDLINE fields `mcp/reads/_util.ts` refuses to select at all.
     *
     * Two calls, any address, from the field app — and it silently removed whoever was
     * really sent, because `setAssignees` takes the whole set. Proven against PGlite before
     * it was closed; `set-assignees.test.ts` now keeps it closed.
     *
     * ⭐ So a tech may move ONE of their stops and may not re-dispatch the board:
     *   · `visit.reschedule`     — `visit:reschedule`  ← held here
     *   · `visit.rescheduleMany` — `visit:update`      ← owner + office
     *   · `visit.setAssignees`   — `visit:update`      ← owner + office
     *   · `visit.cancel`         — `visit:update`      ← owner + office
     *
     * `reschedule` is row-scoped in the service, not here: a tech moves only their own stops,
     * inside the read window (`lockVisitInWindow`, CRM 007), as `getVisit` reads them. Until
     * then it was org-wide, a write the office saw on its board.
     */
    visit: ['read', 'reschedule', 'execute'],
    /**
     * PRD v2 §7: techs collect payment at the door. They record what they took and they may
     * READ BACK THEIR OWN — but they never browse the ledger, and the invoice they see
     * reaches them through their own assigned visit, not through `invoice:read`.
     *
     * ⭐ `read-own` was added by ticket 038 and is deliberately its own verb, not `read`.
     * `get_job` was answering the tech who had just taken $412 with `payment_refs: []` — a
     * fabricated negative about money they were standing there holding. Being told the
     * payment you just took does not exist is worse than being told you may not look.
     *
     * ⚠️ It means EXACTLY "the payments this user recorded". Somebody else's payment on the
     * same job is DECLARED withheld (`WithheldField`), never dropped — a short list that
     * looks complete is the same lie in the other direction. Before this verb existed the
     * behaviour lived only in a docblock in `mcp/reads/schedule.ts`, i.e. the code and the
     * matrix disagreed, which is the exact shape that CAUSED 038.
     */
    payment: ['create', 'read-own'],
    // V3 §6.2: techs restock their own van and count it. `read` is org-wide on purpose —
    // knowing whether the warehouse has the part is what stops a wasted drive. `move` is
    // ROW scoped in the service: every move has to touch their own van.
    inventory: ['read', 'move'],
    // V3 §6.7: the tech at the rooftop unit is the one who knows which unit it was. They
    // can read the register and tag what they touched; editing it stays with the office.
    equipment: ['read', 'tag'],
    // 106: their van and their tools — the service narrows `read` to assigned rows.
    asset: ['read'],
    /**
     * D6: the shop leaderboard, and NOT the report catalogue beside it. The board shows a
     * tech their peers' jobs, arrival record, evidence discipline and what each person
     * collected — a deliberate disclosure, named in the Sonar-experience spec §Security and
     * approved as D6, on the argument that a board nobody but the office can see is not a
     * board. It carries no per-customer detail and no margin, cost rate or shop total.
     *
     * ⚠️ It DOES carry, per person, the money each one recorded — and `recorded_by` is set by
     * whoever typed the payment in, so a cheque taken at the desk lands on the office's row.
     * In a small shop that is close to the month's manual takings, arrived at by addition.
     * That is the disclosure being approved here; the board states it in its own caveats.
     */
    report: ['read-board'],
    // Their own jobs' tickets only (row scoped in the service): flag one from the visit, read
    // it, add messages and photos. Not `triage` (its facts and status are the office's,
    // Giuliano 2026-10-06), not `resolve` (Q4) and not `assign`.
    ticket: ['read', 'create', 'update'],
  },
  bookkeeper: {
    // `browse`: the customer list is collections' worklist (CRM-14). No `job` verb at all.
    customer: ['read', 'browse'],
    invoice: ['read'],
    payment: ['read', 'read-own'],
    // A project is a P&L view, which is exactly what a bookkeeper is here to read.
    project: ['read'],
    // No `read-people` (Q4): the catalogue and the board, not revenue by person.
    report: ['read', 'read-board'],
    billing: ['read'],
  },
};

/**
 * What a caller without `report:read-people` is told: Profit by person's refusal, and the
 * Revenue page's withheld notice (`withheld.reason`). One sentence, so the two cannot drift.
 *
 * ⚠️ It names the roles that hold `report:read-people`, so it must change with
 * `ROLE_PERMISSIONS`.
 */
export const REVENUE_BY_PERSON_REASON =
  'Revenue by person is for the owner and the office.';

/** Client-side affordance gate. The server is the real gate; this hides UI only. */
export function can<R extends Resource>(
  role: Role,
  resource: R,
  action: (typeof STATEMENT)[R][number],
): boolean {
  const perms = ROLE_PERMISSIONS[role][resource] as readonly string[] | undefined;
  return perms?.includes(action) ?? false;
}

/**
 * How much of a job's payment list may this role see? ONE place, so the answer cannot drift
 * between the MCP read, a future screen, and whatever asks next (ticket 038 was caused by
 * exactly that drift — a doctrine comment copied onto a surface it was not written for).
 *
 *   - `'ledger'` — every payment on the job. Holders of `payment:read`.
 *   - `'own'`    — only the ones this user recorded; anyone else's is DECLARED withheld,
 *                  never dropped. The tech at the door.
 *   - `'none'`   — nothing, declared.
 *
 * ⚠️ `read` SUBSUMES `read-own`. Every role holding `read` also LISTS `read-own` (the invite
 * drift detector compares raw sets, so an implied permission would read as office granting
 * `tech` something office lacks). The order below is therefore defence rather than the load
 * -bearing part: a future role that lists `read` and forgets `read-own` still gets 'ledger'.
 */
export type PaymentVisibility = 'ledger' | 'own' | 'none';

export function paymentVisibility(role: Role): PaymentVisibility {
  if (can(role, 'payment', 'read')) return 'ledger';
  if (can(role, 'payment', 'read-own')) return 'own';
  return 'none';
}

/**
 * May `actor` hand out `target` as a role?
 *
 * `member:invite` answers "may this person add someone at all" and stops there — it says
 * nothing about AT WHAT ROLE, which is the whole hole this closes. `office` holds
 * `member:invite` (they hire) and the invite link is printed on screen next to the form,
 * so without this rule an office manager reaches `owner` in two clicks and needs no mailbox
 * to do it.
 *
 * The rule is a LITERAL — only an owner may create another owner — and NOT derived from the
 * permission matrix, even though a subset check over `ROLE_PERMISSIONS` happens to produce
 * the same four answers today. A derivation would silently re-decide this the next time
 * somebody grants `tech` a permission `office` lacks, and that is a membership decision a
 * human should make rather than inherit. `roles.test.ts` pins the literal AGAINST that
 * subset property, so the two are asserted to keep agreeing and a human is told which side
 * moved. See the `mayEnter` precedent in the web router.
 *
 * ⚠️ Advisory on the client, authoritative on the server: `inviteToTeam` re-asks this with
 * the caller's role read from the `member` row, because `ServiceCtx` is `{orgId, userId}`
 * and carries no role for a service to check. `acceptInvite` asks a third time, against the
 * inviter's role — the write-side check cannot reach an invitation that was already
 * written. That third check reads without a lock, so it is "the inviter's role as last
 * committed", not "as it stands at the instant of redemption"; see `acceptInvite`.
 *
 * And none of the three is reachable through Better Auth's own organization endpoints,
 * which write the same tables and enforce none of this — those are refused at the mount
 * (`isSecondDoorToMembership` in `server.ts`). A rule with a second door is a suggestion.
 */
export function canInviteRole(actor: Role, target: Role): boolean {
  if (!can(actor, 'member', 'invite')) return false;
  return target !== 'owner' || actor === 'owner';
}

/** The roles `actor` may offer — what the invite form is allowed to draw. */
export function invitableRolesFor(actor: Role): readonly Role[] {
  return ROLES.filter((r) => canInviteRole(actor, r));
}

/**
 * Who can be sent to a job — the roles that may hold a `visit_assignments` row.
 *
 * ⚠️ This is NOT a permission. `can(role, 'visit', …)` answers "may this role act on
 * visits"; this answers "may this role BE the person on one", which is a different
 * question with a different answer. A bookkeeper may not read a visit *and* may not be
 * sent to one; an `office` dispatcher may create and reschedule every visit in the shop
 * and is still not who turns up. Expressing it as a permission would say the wrong thing
 * in both directions.
 *
 * ⭐ It exists because the rule was being kept in two places that disagreed. The board
 * filtered its lanes client-side — `members.filter(m => m.role === 'tech' || m.role ===
 * 'owner')`, inline in `SchedulePage.tsx` — while both assignee pickers offered
 * `org.members` UNFILTERED. So a bookkeeper could be assigned a visit from a creation
 * sheet and then land on a board that has no lane to draw them in: work assigned to
 * someone the dispatch screen cannot show. One predicate, imported by the picker, the lane
 * and the server that refuses the write.
 *
 * `owner` is in the list on purpose: in a graduating shop the owner still turns a wrench,
 * and today's board already gives them a lane.
 */
export const DISPATCHABLE_ROLES = ['owner', 'tech'] as const satisfies readonly Role[];

/** Whether someone in this role may be assigned to a visit. */
export function canHoldVisits(role: Role): boolean {
  return (DISPATCHABLE_ROLES as readonly Role[]).includes(role);
}
